import React, { useEffect, useState } from 'react'
import { useTranslation } from '@structura/i18n'

interface LoadCaseSummary {
  id: string
  name: string
  category: string
  isActive: boolean
  displayOrder: number
  loadCount: number
}

interface LoadCaseGroups {
  [category: string]: LoadCaseSummary[]
}

export interface LoadsSectionProps {
  getSessionHeaders: () => HeadersInit
}

const LoadsSection: React.FC<LoadsSectionProps> = ({ getSessionHeaders }) => {
  const { t } = useTranslation()
  const [loadCasesLoading, setLoadCasesLoading] = useState<boolean>(false)
  const [loadCasesError, setLoadCasesError] = useState<string | null>(null)
  const [loadCaseGroups, setLoadCaseGroups] = useState<LoadCaseGroups>({})

  useEffect(() => {
    const loadLoadCases = async (): Promise<void> => {
      setLoadCasesLoading(true)
      setLoadCasesError(null)
      try {
        const response = await fetch('/api/AdminLoads/cases', {
          headers: getSessionHeaders()
        })

        if (!response.ok) {
          throw new Error(t('admin.loads.errLoadHttp', { status: response.status }))
        }

        const cases: LoadCaseSummary[] = await response.json()
        const groups: LoadCaseGroups = {}
        for (let index = 0; index < cases.length; index++) {
          const loadCase = cases[index]
          const key = loadCase.category || 'Uncategorized'
          if (!groups[key]) {
            groups[key] = []
          }
          groups[key].push(loadCase)
        }

        setLoadCaseGroups(groups)
      } catch (err) {
        const message = (err as Error).message || t('admin.loads.errLoad')
        setLoadCasesError(message)
      } finally {
        setLoadCasesLoading(false)
      }
    }

    void loadLoadCases()
  }, [getSessionHeaders, t])

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-arrow-down-up me-2"></i>
          {t('admin.loads.title')}
        </h5>
      </div>
      <div className="card-body">
        <p className="text-muted">
          {t('admin.loads.introBefore')} <code>Loads.db</code>{t('admin.loads.introAfter')}
        </p>

        {loadCasesLoading && (
          <div className="d-flex align-items-center mb-3">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <span>{t('admin.loads.loading')}</span>
          </div>
        )}

        {loadCasesError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {loadCasesError}
            <button type="button" className="btn-close" onClick={() => setLoadCasesError(null)}></button>
          </div>
        )}

        {!loadCasesLoading && !loadCasesError && (
          <div className="mt-3">
            <div className="row g-3">
              {Object.keys(loadCaseGroups).map((category) => {
                const items = loadCaseGroups[category] || []
                if (items.length === 0) {
                  return null
                }

                return (
                  <div key={category} className="col-12">
                    <div className="card border-secondary">
                      <div className="card-header bg-light">
                        <h6 className="mb-0 text-capitalize">
                          <i className="bi bi-layers me-2"></i>
                          {category} {t('admin.loads.loadCasesSuffix')}
                        </h6>
                      </div>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>{t('admin.loads.thName')}</th>
                                <th className="text-center">{t('admin.loads.thActive')}</th>
                                <th className="text-end">{t('admin.loads.thOrder')}</th>
                                <th className="text-end">{t('admin.loads.thLoads')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((loadCase) => (
                                <tr key={loadCase.id}>
                                  <td>{loadCase.name}</td>
                                  <td className="text-center">
                                    {loadCase.isActive ? (
                                      <span className="badge bg-success-subtle text-success">{t('admin.users.statusActive')}</span>
                                    ) : (
                                      <span className="badge bg-secondary-subtle text-secondary">{t('admin.loads.statusInactive')}</span>
                                    )}
                                  </td>
                                  <td className="text-end">{loadCase.displayOrder}</td>
                                  <td className="text-end">{loadCase.loadCount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}

              {Object.keys(loadCaseGroups).length === 0 && (
                <div className="col-12">
                  <div className="alert alert-info mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    {t('admin.loads.noLoadCasesBefore')} <code>Loads.db</code>{t('admin.loads.noLoadCasesAfter')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LoadsSection
